import React from 'react';
import { TruncatedText } from 'design-system/src/components/TruncatedText/TruncatedText';
import { emptyTableCellCopy } from 'design-system/src/sharedCopy';
import { doesSearchMatch, type SearchFieldType } from './utils';

const SEARCH_HIGHLIGHT_CLASS = 'bg-purple-100';

export interface SearchHighlightTextProps {
  /** The search term to match against */
  searchTerm: string;
  /** The value to display and match against */
  value: string | null | undefined;
  /** The type of field for matching logic */
  fieldType: SearchFieldType;
  /** Fallback text when value is empty */
  fallback?: string;
  /** Whether to truncate text with TruncatedText (default: false) */
  truncate?: boolean;
  /** Number of lines before truncating (only used when truncate is true) */
  lines?: number;
}

/**
 * Component that renders text with search highlighting and optional truncation.
 * Automatically highlights text when the search term matches based on the field type.
 */
export const SearchHighlightText: React.FC<SearchHighlightTextProps> = ({
  searchTerm,
  value,
  fieldType,
  fallback = emptyTableCellCopy,
  truncate = false,
  lines = 1,
}) => {
  const isMatch = doesSearchMatch(searchTerm, value, fieldType);
  const displayValue = value || fallback;

  if (truncate) {
    return (
      <TruncatedText lines={lines} className={isMatch ? SEARCH_HIGHLIGHT_CLASS : undefined} tooltipPlacement="bottom">
        {displayValue}
      </TruncatedText>
    );
  }

  return <span className={isMatch ? SEARCH_HIGHLIGHT_CLASS : undefined}>{displayValue}</span>;
};
